/**
 * Yallmark collection page: filters, sort and pagination without full page reloads.
 *
 * Everything works without JavaScript (plain GET forms and links). This script:
 * - submits [data-ym-filter-form] forms and follows [data-ym-link] links through the
 *   Section Rendering API, swaps the section in place and updates the URL;
 * - submits on sort change, and when a desktop filter popover closes with changes;
 * - keeps one popover open at a time, closes it on outside click or Escape;
 * - opens/closes the mobile bottom sheet and live-updates its "Show N products" button.
 */
(function () {
  if (window.ymCollectionInit) return;
  window.ymCollectionInit = true;

  /** @returns {HTMLElement | null} */
  const root = () => document.querySelector('[data-ym-collection]');

  /** @param {HTMLFormElement} form */
  function formUrl(form) {
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form)) {
      if (typeof value === 'string' && value !== '') params.append(key, value);
    }
    const query = params.toString();
    return form.action.split('?')[0] + (query ? `?${query}` : '');
  }

  /** @param {string} url @param {string} sectionId */
  function sectionUrl(url, sectionId) {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', sectionId);
    return u.toString();
  }

  /** @type {AbortController | null} */
  let pending = null;

  /**
   * @param {string} url
   * @param {{ push?: boolean, scroll?: boolean }} [options]
   */
  async function render(url, { push = true, scroll = false } = {}) {
    const el = root();
    if (!el) {
      window.location.href = url;
      return;
    }
    const sectionId = el.dataset.sectionId || '';
    const wrapper = document.getElementById(`shopify-section-${sectionId}`);
    if (!wrapper) {
      window.location.href = url;
      return;
    }

    pending?.abort();
    pending = new AbortController();
    el.classList.add('is-loading');

    try {
      const response = await fetch(sectionUrl(url, sectionId), { signal: pending.signal });
      if (!response.ok) throw new Error(String(response.status));
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.getElementById(`shopify-section-${sectionId}`);
      if (!fresh) throw new Error('section missing');

      wrapper.innerHTML = fresh.innerHTML;
      if (push) history.pushState({ ymCollection: true }, '', url);

      if (scroll) {
        const grid = document.getElementById(`YmGrid-${sectionId}`);
        grid?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      announce(wrapper);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      window.location.href = url;
    }
  }

  /** Read the new product count to screen readers. @param {HTMLElement} wrapper */
  function announce(wrapper) {
    const tools = wrapper.querySelector('[data-ym-count]');
    if (!tools) return;
    let live = document.getElementById('YmCollectionLive');
    if (!live) {
      live = document.createElement('div');
      live.id = 'YmCollectionLive';
      live.className = 'ym-visually-hidden';
      live.setAttribute('role', 'status');
      document.body.appendChild(live);
    }
    const n = Number(tools.getAttribute('data-ym-count'));
    live.textContent = `${n} ${n === 1 ? 'product' : 'products'}`;
  }

  /** @param {HTMLElement} el */
  function closePopovers(except) {
    document.querySelectorAll('details[data-ym-pop][open]').forEach((d) => {
      if (d !== except) /** @type {HTMLDetailsElement} */ (d).open = false;
    });
  }

  // Links: chips, clear, pagination, quick filters
  document.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const link = target.closest('a[data-ym-link]');
    if (link instanceof HTMLAnchorElement && root()?.contains(link)) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      const sheet = link.closest('dialog');
      if (sheet instanceof HTMLDialogElement) sheet.close();
      render(link.href, { scroll: link.hasAttribute('data-ym-scroll') });
      return;
    }

    const opener = target.closest('[data-ym-sheet-open]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('data-ym-sheet-open') || '');
      if (dialog instanceof HTMLDialogElement) dialog.showModal();
      return;
    }

    if (target.closest('[data-ym-sheet-close]')) {
      target.closest('dialog')?.close();
      return;
    }

    // Backdrop click closes the sheet
    if (target instanceof HTMLDialogElement && target.classList.contains('ym-sheet')) {
      target.close();
      return;
    }

    // Outside click closes popovers (which applies their changes)
    if (!target.closest('details[data-ym-pop]')) closePopovers(null);
  });

  // Forms: filters and sort
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-ym-filter-form]')) return;
    event.preventDefault();
    form.dataset.dirty = '';
    form.closest('dialog')?.close();
    closePopovers(null);
    render(formUrl(form));
  });

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLElement)) return;
    const form = input.closest('form[data-ym-filter-form]');
    if (!(form instanceof HTMLFormElement)) return;

    if (input.matches('[data-ym-sort]')) {
      form.requestSubmit();
      return;
    }

    form.dataset.dirty = 'true';
    if (form.matches('[data-ym-sheet-form]')) previewCount(form);
  });

  // Desktop popovers: one open at a time; closing with changes applies them
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.matches('[data-ym-pop]')) return;
      if (details.open) {
        closePopovers(details);
        return;
      }
      const form = details.closest('form');
      if (form instanceof HTMLFormElement && form.dataset.dirty === 'true') form.requestSubmit();
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector('details[data-ym-pop][open]');
    if (open instanceof HTMLDetailsElement) {
      open.open = false;
      open.querySelector('summary')?.focus();
    }
  });

  /** @type {AbortController | null} */
  let countRequest = null;

  /** Update "Show N products" in the sheet while choosing. @param {HTMLFormElement} form */
  async function previewCount(form) {
    const el = root();
    const button = form.querySelector('[data-ym-sheet-submit]');
    if (!el || !button) return;
    countRequest?.abort();
    countRequest = new AbortController();
    try {
      const response = await fetch(sectionUrl(formUrl(form), el.dataset.sectionId || ''), {
        signal: countRequest.signal,
      });
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const n = Number(doc.querySelector('[data-ym-count]')?.getAttribute('data-ym-count'));
      if (!Number.isNaN(n)) button.textContent = `Show ${n} ${n === 1 ? 'product' : 'products'}`;
    } catch (_) {
      /* keep the old label */
    }
  }

  window.addEventListener('popstate', () => {
    if (root()) render(window.location.href, { push: false });
  });
})();
